import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Redirect to root on sign-out or missing session at startup
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !session)) {
    if (window.location.pathname !== '/dashboard') {
      window.location.href = '/dashboard';
    }
  }
});

export async function signUp(email, password) {
  return await supabase.auth.signUp({ email, password });
}

export async function signIn(email, password) {
  return await supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  return await supabase.auth.signOut();
}

export async function getRole(session) {
  const { data } = await supabase.rpc('get_my_role');
  return data || 'resident';
}

export async function createRequest(requestData) {
  // LEU is now server-derived by enforce_booking_session_validity trigger.
  // Client must provide capture_session_id; trigger derives confirmed_leu from capture_sessions.leu.
  if (!requestData.capture_session_id) {
    throw new Error("SESSION_REQUIRED: Booking requires a valid capture session");
  }
  if (!requestData.size) {
    throw new Error("BOOKING_GUARD: canonical size/tier is required for request creation");
  }
  const { error } = await supabase.from('requests').insert([requestData]);
  if (error) {
    console.error("[createRequest] INSERT failed", {
      message: error.message, details: error.details, hint: error.hint, code: error.code,
      context: { capture_session_id: requestData.capture_session_id, status: requestData.status, size: requestData.size, date: requestData.date, zone: requestData.zone },
    });
    throw error;
  }
  const { data, error: fetchError } = await supabase
    .from('requests')
    .select('*, payments(*)')
    .eq('user_id', requestData.user_id)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  if (fetchError) throw fetchError;
  return data;
}

/** Persist capture session record */
export async function createCaptureSession(sessionData) {
  const { data, error } = await supabase.from('capture_sessions').insert([sessionData]).select().single();
  if (error) {
    console.debug("[CAPTURE_SESSION] insert_failed", { code: error.code, message: error.message?.slice(0, 120) });
    return null; // Non-fatal — session data is also kept client-side
  }
  return data;
}

/** Persist capture photo record */
export async function createCapturePhoto(photoData) {
  const { data, error } = await supabase.from('capture_photos').insert([photoData]).select().single();
  if (error) {
    console.debug("[CAPTURE_PHOTO] insert_failed", { code: error.code, message: error.message?.slice(0, 120) });
    return null; // Non-fatal
  }
  return data;
}

export async function getMyRequests(userId) {
  const { data, error } = await supabase.from('requests').select('*, payments(*)').eq('user_id', userId).order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getAllRequests() {
  const { data, error } = await supabase.from('requests').select('*, payments(*)').order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function updateRequestStatus(id, status, extraFields = {}) {
  const { data, error } = await supabase.from('requests').update({ status, ...extraFields }).eq('id', id).select();
  if (error) throw new Error('Supabase error: ' + error.message);
  if (!data || data.length === 0) throw new Error('RLS_BLOCKED');
  return data;
}

export async function createPaymentRecord({ request_id, method, amount, status }) {
  const { data, error } = await supabase.from('payments').insert([{ request_id, method, amount, status: status || 'pending_verification' }]).select().single();
  if (error) throw error;
  return data;
}

export async function verifyManualPayment(requestId) {
  const { data, error } = await supabase.from('payments').update({ status: 'verified' }).eq('request_id', requestId);
  if (error) throw error;
  return data;
}

export async function uploadPhoto(file, userId) {
  const ext = file.name ? file.name.split('.').pop() : 'jpg';
  const mime = file.type || 'image/jpeg';
  const path = `requests/${userId}/${Date.now()}.${ext}`;
  try {
    const { error } = await supabase.storage.from('request-photos').upload(path, file, { upsert: true, contentType: mime });
    if (error) {
      if (error.message && (error.message.includes('Invalid Refresh Token') || error.message.includes('Refresh Token Not Found'))) {
        throw new Error('SESSION_EXPIRED');
      }
      throw error;
    }
  } catch (e) {
    if (e.message === 'SESSION_EXPIRED') throw e;
    if (e.message && (e.message.includes('Invalid Refresh Token') || e.message.includes('Refresh Token Not Found'))) {
      throw new Error('SESSION_EXPIRED');
    }
    throw e;
  }
  const { data: { publicUrl } } = supabase.storage.from('request-photos').getPublicUrl(path);
  return publicUrl;
}

export function calcCancellationFee(request) {
  const status = request.status;
  const pay = request.payments?.length ? [...request.payments].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] : null;
  const total = pay?.amount || 0;
  if (['complete','canceled_by_customer','canceled_by_admin','in_progress'].includes(status)) {
    return { canCancel: false, feePercent: 0, feeAmount: 0, refundAmount: 0 };
  }

  // Pickup window start times: Tuesday 10 AM, Saturday 12 PM
  // Fee window is 2 hours before pickup start
  const now = Date.now();
  let pickupStart = null;
  try {
    const pickupWindow = request.pickup_window || "";
    if (request.date) {
      const baseDate = new Date(request.date);
      if (!isNaN(baseDate.getTime())) {
        // Extract hour from pickup window string (e.g. "10:00 AM – 1:00 PM")
        const match = pickupWindow.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (match) {
          let hour = parseInt(match[1]);
          const min = parseInt(match[2]);
          const ampm = match[3].toUpperCase();
          if (ampm === "PM" && hour !== 12) hour += 12;
          if (ampm === "AM" && hour === 12) hour = 0;
          baseDate.setHours(hour, min, 0, 0);
          pickupStart = baseDate.getTime();
        }
      }
    }
  } catch { /* non-fatal — fall back to booking time */ }

  // Hours until pickup (negative = past pickup)
  const hoursUntilPickup = pickupStart ? (pickupStart - now) / (1000 * 60 * 60) : null;
  // Free window: more than 2 hours before pickup start
  const withinFreeWindow = hoursUntilPickup === null || hoursUntilPickup > 2;
  const minutesRemaining = hoursUntilPickup !== null ? Math.max(0, Math.round((hoursUntilPickup - 0) * 60)) : 0;

  let feePercent = 0;
  if (status === 'en_route') {
    feePercent = 60;
  } else if (status === 'scheduled') {
    feePercent = withinFreeWindow ? 0 : 20;
  } else if (status === 'pending' || status === 'checkout_pending') {
    feePercent = 0; // pending is always free
  }
  const feeAmount = Math.round(total * feePercent / 100);
  const refundAmount = total - feeAmount;
  return { canCancel: true, feePercent, feeAmount, refundAmount, withinFreeWindow, minutesRemaining };
}

export async function cancelRequest({ requestId, reason, feePercent, feeAmount, refundAmount }) {
  const { data, error } = await supabase.from('requests').update({
    status: 'canceled_by_customer',
    cancellation_reason: reason || 'Customer requested cancellation',
    cancellation_fee_percent: feePercent,
    cancellation_fee_amount: feeAmount,
    refund_amount: refundAmount,
    refund_status: refundAmount > 0 ? 'pending' : 'none',
  }).eq('id', requestId);
  if (error) throw error;
  return data;
}

export async function adminOverrideCancellation({ requestId, feePercent, feeAmount, refundAmount, overrideReason, adminId }) {
  const { data, error } = await supabase.from('requests').update({
    cancellation_fee_percent: feePercent,
    cancellation_fee_amount: feeAmount,
    refund_amount: refundAmount,
    refund_status: refundAmount > 0 ? 'pending' : 'none',
    override_reason: overrideReason,
    override_admin_id: adminId,
  }).eq('id', requestId);
  if (error) throw error;
  return data;
}

export async function updateRefundStatus(requestId, refundStatus) {
  const { data, error } = await supabase.from('requests').update({ refund_status: refundStatus }).eq('id', requestId);
  if (error) throw error;
  return data;
}

export async function sendConfirmationEmail({ email, booking }, accessToken) {
  try {
    const { data, error } = await supabase.functions.invoke('send-confirmation', {
      body: { email, booking },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.log('Email send error (non-fatal):', err);
  }
}

export async function clearDeclinedRequests() {
  const { data, error } = await supabase.from('requests').update({ archived: true }).eq('status', 'declined');
  if (error) throw new Error(error.message);
  return data?.length || 0;
}

export async function clearArchivedRequests(adminId) {
  const { generateArchiveTag } = await import('./utils/businessTime');
  const { data: rows, error: fetchErr } = await supabase
    .from('requests')
    .select('id')
    .eq('archived', true)
    .eq('archive_cleared', false);
  if (fetchErr) throw new Error(fetchErr.message);
  if (!rows || rows.length === 0) return 0;
  const now = new Date().toISOString();
  for (const row of rows) {
    const tag = generateArchiveTag();
    await supabase.from('requests').update({
      archive_cleared: true,
      archive_tag_id: tag,
      archive_cleared_at: now,
      archive_cleared_by: adminId || null,
    }).eq('id', row.id);
  }
  return rows.length;
}

export async function voidPayment({ requestId, reason, adminId }) {
  const now = new Date().toISOString();
  const { error } = await supabase.from('payments').update({
    status: 'voided',
    void_reason: reason || 'Admin reversal',
    voided_at: now,
    voided_by: adminId || null,
  }).eq('request_id', requestId);
  if (error) throw new Error(error.message);
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

export async function completeJob({
  requestId,
  providerVerifiedLeu,
  confirmedStaging,
  flags,
  systemAssignedLeu,
  systemAssignedTier,
  driverVerifiedTier,
  completionPhotoUrl,
  completedBy,
  predictedLeuSnapshot,
  companyMarginAmt,
  driverPayAmt,
  variableCostAmt,
  marketId,
  zoneCostSnapshot,
}) {
  // LEU enforcement: provider-verified LEU is required for job completion
  if (providerVerifiedLeu == null) {
    throw new Error("VERIFIED_LEU_REQUIRED: Job completion requires verified LEU");
  }
  const now = new Date().toISOString();

  // Compute prediction variance at execution time
  const varianceLeu = systemAssignedLeu != null && providerVerifiedLeu != null
    ? providerVerifiedLeu - systemAssignedLeu
    : null;

  // Structured LEU logging
  if (varianceLeu != null && Math.abs(varianceLeu) > 1) {
    console.log("[LEU_MISMATCH]", JSON.stringify({ event: "LEU_MISMATCH", requestId, systemAssignedLeu, providerVerifiedLeu, delta: varianceLeu }));
  }

  const varianceTier = (systemAssignedTier && driverVerifiedTier && systemAssignedTier !== driverVerifiedTier)
    ? (providerVerifiedLeu > (systemAssignedLeu || 0) ? 'underreported' : 'overreported')
    : (systemAssignedTier && driverVerifiedTier ? 'match' : null);

  const { data, error } = await supabase
    .from('requests')
    .update({
      status:                     'complete',
      provider_verified_leu:      providerVerifiedLeu,
      prediction_variance_leu:    varianceLeu,
      prediction_variance_tier:   varianceTier,
      confirmed_staging_location: confirmedStaging,
      verified_at:                now,
      job_completed_at:           now,
      misrep_flagged:             flags.length > 0,
      data_quality_flags:         flags,
      driver_verified_tier:       driverVerifiedTier || null,
      driver_verified_at:         now,
      driver_issue_flags:         flags,
      completion_photo_url:       completionPhotoUrl || null,
      completed_at:               now,
      completed_by:               completedBy || null,
      predicted_leu_snapshot:     predictedLeuSnapshot ?? null,
      verification_source:        'driver_on_site',
      company_margin_amt:         companyMarginAmt ?? null,
      driver_pay_amt:             driverPayAmt ?? null,
      variable_cost_amt:          variableCostAmt ?? null,
      market_id:                  marketId ?? null,
      zone_cost_snapshot:         zoneCostSnapshot ?? null,
    })
    .eq('id', requestId)
    .select();

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('RLS_BLOCKED');
  return data;
}

/** Record a tier mismatch event (non-fatal) */
export async function recordTierMismatch({
  request_id = null,
  capture_session_id,
  system_assigned_tier,
  customer_selected_tier,
  override_direction,
  suggested_leu = null,
  confirmed_leu = null,
  composite_confidence_score = null,
}) {
  try {
    const { data, error } = await supabase.from('tier_mismatch_events').insert([{
      request_id,
      capture_session_id,
      system_assigned_tier,
      customer_selected_tier,
      override_direction,
      suggested_leu,
      confirmed_leu,
      composite_confidence_score,
      occurred_at: new Date().toISOString(),
    }]).select().single();
    if (error) {
      console.debug("[TIER_MISMATCH] insert_failed", { code: error.code, message: error.message?.slice(0, 120) });
      return null;
    }
    return data;
  } catch (e) {
    console.debug("[TIER_MISMATCH] insert_failed", { message: e?.message?.slice(0, 120) });
    return null;
  }
}

/** Load driver users from user_roles. Returns [{ user_id, role }] */
export async function getDriverUsers() {
  const { data, error } = await supabase.rpc('get_driver_list');
  if (error || !data) {
    // Fallback: try direct query (may fail due to RLS)
    const { data: d2, error: e2 } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'driver');
    if (e2 || !d2) return [];
    return d2.map(r => ({ value: r.user_id, label: r.user_id }));
  }
  return (data || []).map(r => ({ value: r.user_id, label: r.email || r.user_id }));
}

/** Fetch zone/date capacity totals (all users, read-only aggregate) */
export async function getZoneCapacity() {
  const { data, error } = await supabase.rpc('get_zone_capacity');
  if (error) { console.debug("[ZONE_CAPACITY] rpc_failed", error.message); return []; }
  return data || [];
}

/** Assign a driver to a request */
export async function assignDriver(requestId, driverUserId) {
  // Read current state for idempotency check
  const { data: current } = await supabase
    .from('requests')
    .select('assigned_driver, driver_notified_at, addr, date, pickup_window')
    .eq('id', requestId)
    .single();

  const { data, error } = await supabase
    .from('requests')
    .update({ assigned_driver: driverUserId })
    .eq('id', requestId)
    .select();
  if (error) throw error;

  // Notify driver on new assignment (null → driver, or driver A → driver B)
  if (driverUserId && driverUserId !== current?.assigned_driver) {
    try {
      // Look up driver email to find their contact info
      const { data: driverData } = await supabase.rpc('get_driver_list');
      const driver = (driverData || []).find(d => d.user_id === driverUserId);
      if (driver?.email) {
        // Use email as SMS lookup — drivers may not have phone in user_roles
        // For now, log the assignment. Full SMS requires driver phone field.
        console.log("[assignDriver] Driver notified:", { requestId, driverUserId, email: driver.email });
      }
      await supabase.from('requests').update({ driver_notified_at: new Date().toISOString() }).eq('id', requestId);
    } catch (notifyErr) {
      console.debug("[assignDriver] Notification failed (non-fatal):", notifyErr?.message);
    }
  }

  return data;
}

/** Record operator agreement signature (immutable — one row per version accepted) */
export async function recordAgreement() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { error } = await supabase.from("operator_agreements").insert([{
    user_id: user.id,
    agreed_at: new Date().toISOString(),
    agreement_version: "1.1",
  }]);
  if (error) throw error;
}

/** Check whether the current user has signed an operator agreement */
export async function hasOperatorAgreement() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data, error } = await supabase
    .from("operator_agreements")
    .select("id")
    .eq("user_id", user.id)
    .limit(1);
  if (error) return false;
  return data && data.length > 0;
}
